/**
 * Pure audio DSP for backend STT (#93, D-STT).
 *
 * Amazon Transcribe streaming expects raw signed 16-bit little-endian PCM, mono,
 * 16 kHz. The browser's Web Audio graph hands us `Float32Array` frames in the
 * `AudioContext` sample rate (often 44.1/48 kHz even when we request 16 kHz — the
 * request is a hint, not a guarantee). These pure helpers do the two conversions the
 * capture path needs and are unit-tested in isolation so the (untestable) Web Audio
 * plumbing stays thin.
 */

/**
 * Convert Float32 samples in [-1, 1] to signed 16-bit PCM. Values are clamped so a
 * hot mic can't wrap around. (`Int16Array` is little-endian on every browser
 * platform, matching the Transcribe wire format.)
 */
export function floatTo16BitPCM(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i] ?? 0));
    // Asymmetric scale: negative range is one larger than positive in two's complement.
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
  }
  return out;
}

/**
 * Nearest-sample decimation from `inRate` down to `outRate` (never upsamples — the
 * capture rate is always ≥ 16 kHz in practice). Returns the input unchanged when the
 * rates match or `outRate` is not a smaller positive rate.
 */
export function downsampleFloat32(
  input: Float32Array,
  inRate: number,
  outRate: number,
): Float32Array {
  if (outRate <= 0 || outRate >= inRate) return input;
  const ratio = inRate / outRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    out[i] = input[Math.floor(i * ratio)] ?? 0;
  }
  return out;
}

/**
 * Full capture-frame encode: downsample to `outRate` (default 16 kHz) then convert to
 * 16-bit PCM. The default Web Audio {@link PcmSource} uses this per frame.
 */
export function encodePcmFrame(
  input: Float32Array,
  inRate: number,
  outRate = 16000,
): Int16Array {
  return floatTo16BitPCM(downsampleFloat32(input, inRate, outRate));
}
